import {Portal, Root, Trigger, Content} from "@radix-ui/react-dialog";
import {ImageIcon} from "@radix-ui/react-icons";
import {type ReactNode} from "react";
import Markdown from "react-markdown";
import type {ChangelogResp} from "data/changelog/api";
import {ChangelogHeader} from "./changelog-header";

import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "@sensoteq/styles/dist/sensoteq-tailwind-config.cjs";
import {useRootStore} from "stores";
import type UIStore from "stores/UIStore";
const fullConfig = resolveConfig(tailwindConfig);
const {theme} = fullConfig;

type Props = {
  children: ReactNode;
  changelog: ChangelogResp;
  releasedAt: string;
  isImagesFetched: boolean;
  imageCache?: Map<string, HTMLImageElement>;
};

export function ChangelogOverview({children, changelog, releasedAt, isImagesFetched, imageCache}: Props) {
  const {uiStore} = useRootStore() as unknown as {uiStore: UIStore};
  return (
    <Root>
      <Trigger
        onClick={() => uiStore.setSeenChangelog(changelog.changelog_id)}
        asChild
      >
        {children}
      </Trigger>
      <Portal>
        <Content
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: 580,
            width: "100%",
            height: "95%",
            fontFamily: "Roboto, sans-serif",
            zIndex: 90,
            backgroundColor: theme.colors.popover,
            borderColor: theme.colors["content-placeholder"],
          }}
          className="border flex rounded-lg focus:outline-none data-[state=open]:animate-contentShow"
        >
          <div className="flex flex-col flex-1">
            <ChangelogHeader
              releasedAt={releasedAt}
              changelog={changelog}
            />
            <div
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.colors["content-placeholder"]} ${theme.colors.secondary}`,
              }}
              className="px-12 overflow-y-auto pb-10"
            >
              {changelog.summary.length > 0 && (
                <div
                  style={{
                    paddingTop: 20,
                    paddingBottom: 5,
                    fontSize: theme.fontSize.large,
                    color: theme.colors["secondary-foreground"],
                  }}
                >
                  {changelog.summary}
                </div>
              )}
              <Markdown
                renderers={{
                  paragraph: ({children}) => {
                    return (
                      <p
                        style={{
                          marginTop: 10,
                          fontSize: "1.5rem",
                          lineHeight: theme.lineHeight.normal,
                          color: theme.colors["secondary-foreground-alt"],
                        }}
                      >
                        {children}
                      </p>
                    );
                  },
                  heading: ({children}) => {
                    return (
                      <div
                        style={{
                          marginTop: 20,
                          marginBottom: 20,
                          fontSize: theme.fontSize.slightlyLarge,
                          color: theme.colors["secondary-foreground"],
                        }}
                      >
                        {children}
                      </div>
                    );
                  },
                  image: (props: {src: string; alt: string}) => {
                    const img = imageCache?.get(props.src);

                    if (!isImagesFetched) {
                      return (
                        <div
                          className="animate-pulse bg-secondary-skeleton rounded-md flex flex-row items-center justify-center"
                          style={{width: "100%", height: 250, marginTop: 10}}
                        >
                          <ImageIcon
                            className="text-gray-400 dark:text-gray-600"
                            style={{width: 150, height: 80}}
                          />
                        </div>
                      );
                    }
                    if (!img) return null;
                    return (
                      <div
                        className="flex flex-row"
                        style={{maxHeight: 320, paddingTop: 10}}
                        ref={(div) => div?.appendChild(img)}
                      ></div>
                    );
                  },
                  thematicBreak: () => {
                    return <hr style={{marginTop: 15}}></hr>;
                  },
                }}
              >
                {changelog.body.markdown}
              </Markdown>
            </div>
          </div>
        </Content>
      </Portal>
    </Root>
  );
}
